// crewbit-admin/src/components/money/T2125SectionRow.tsx
//
// Section header row inside ExpensesTable. Renders the CRA T2125 line label
// + count + subtotal for one t2125_line bucket. Visual divider only — no
// click affordance v1.
//
// Naming note: file/component intentionally suffixed `Row` to avoid the
// type-vs-component name shadow with `T2125Section` interface from
// `lib/t2125Mapping.ts`. Per Session 129 §2.3 / F2 lesson.
//
// Spec: docs/references/MONEY_MODULE_SPEC_v1.md §Expenses.7.2 (T2125 grouping).

import { palette, spacing } from '../../styles/tokens';
import { formatMoney } from '../../lib/money';

interface T2125SectionRowProps {
  craLine: string;
  groupLabel: string;
  count: number;
  subtotal: number;
  /** Number of columns to span — must match ExpensesTable column count. */
  colSpan: number;
}

export function T2125SectionRow({
  craLine,
  groupLabel,
  count,
  subtotal,
  colSpan,
}: T2125SectionRowProps) {
  const lineSuffix = craLine === 'uncategorized' ? '' : ` · CRA line ${craLine}`;
  return (
    <tr style={{ background: palette.g50 }}>
      <td
        colSpan={colSpan}
        style={{
          padding: `${spacing.sm}px ${spacing.md}px`,
          borderTop: `1px solid ${palette.g200}`,
          borderBottom: `1px solid ${palette.g150}`,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: spacing.md,
          }}
        >
          <div
            style={{
              fontSize: 12,
              fontWeight: 700,
              color: palette.g700,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
            }}
          >
            {groupLabel}
            <span style={{ fontWeight: 500, color: palette.g500, marginLeft: 8 }}>
              {count} {count === 1 ? 'expense' : 'expenses'}{lineSuffix}
            </span>
          </div>
          <div
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: palette.g700,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {formatMoney(subtotal)}
          </div>
        </div>
      </td>
    </tr>
  );
}
